/**
 * Split-panel frame for the sign-in / request-access screens.
 *
 * Left: the deep-CDM-blue brand panel with the ammonite watermark and the
 * product name. Right: the form card, topped by the Ciments du Maroc logo.
 * On phones the brand panel collapses away and only the form remains.
 */
import { Box, Paper, Typography, useTheme } from "@mui/material";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import logo from "../assets/cdm-logo.png";

export function AuthLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { tokens } = theme;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Brand panel — hidden on phones. */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          flex: "0 0 42%",
          maxWidth: 560,
          p: 8,
          color: "common.white",
          background: `linear-gradient(165deg, ${tokens.sidebar.bg} 0%, #002a4d 60%, #001d38 100%)`,
        }}
      >
        {/* Ammonite spiral watermark. */}
        <Box
          component="svg"
          viewBox="0 0 512 512"
          aria-hidden
          sx={{
            position: "absolute",
            right: -180,
            bottom: -200,
            width: 640,
            height: 640,
            transform: "rotate(-14deg)",
            opacity: 0.09,
          }}
        >
          <path
            d="M 100 256 A 156 156 0 1 1 412 256 A 120 120 0 1 1 172 256 A 92 92 0 1 1 356 256 A 70 70 0 1 1 216 256 A 53 53 0 1 1 322 256 A 40 40 0 1 1 242 256"
            fill="none"
            stroke="#ffffff"
            strokeWidth={14}
            strokeLinecap="round"
          />
        </Box>
        <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
            {t("app.title")}
          </Typography>
          <Box sx={{ width: 52, height: 4, bgcolor: "secondary.main", borderRadius: 1 }} />
          <Typography sx={{ maxWidth: 360, color: "rgba(255,255,255,0.82)", lineHeight: 1.65 }}>
            {t("auth.brandTagline")}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{ position: "absolute", left: 64, bottom: 40, color: "rgba(255,255,255,0.7)" }}
        >
          Ciments du Maroc — HeidelbergCement Group
        </Typography>
      </Box>

      {/* Form panel. */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, md: 6 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 400,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            border: "none",
            bgcolor: "transparent",
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Ciments du Maroc"
            sx={{ width: 216, maxWidth: "100%", alignSelf: "flex-start" }}
          />
          {children}
        </Paper>
      </Box>
    </Box>
  );
}
